import { isAdminType, UserType } from "../../auth/authApi";
import { getAuthPayload, getPrincipalRoleFromToken, getRevokedModulesFromToken } from "../../../utils/authToken";

/**
 * O que o administrador restrito pode fazer na gestão de usuários (ADR 0027).
 * Espelho das regras do backend (`auth.service`), que é quem decide — aqui só
 * se esconde o que ele recusaria.
 */

/** Módulos que cada papel alcança: espelho do `MODULES_OF_ROLE` do backend. */
const MODULES_OF_ROLE: Partial<Record<UserType, string[]>> = {
  SUPERVISOR: ["OPERACOES", "ESTOQUE"],
  SUPERVISOR_DE_CAMPO: ["OPERACOES", "ESTOQUE"],
  DP: ["DP"],
  FINANCEIRO: ["FINANCEIRO"],
  FINANCEIRO_LEITURA: ["FINANCEIRO"],
  GESTOR_CAMPANHAS: ["CAMPANHAS"],
};

/** Tipos que têm lojas vinculadas (`UserStore`): as do supervisor de campo entram na cobertura semanal. */
export const SUPERVISOR_TYPES: UserType[] = ["SUPERVISOR", "SUPERVISOR_DE_CAMPO"];

/** Tipos na ordem do seletor, com o rótulo do formulário. */
export const USER_TYPE_OPTIONS: { value: UserType; label: string }[] = [
  { value: "SUPERVISOR", label: "Supervisor" },
  { value: "SUPERVISOR_DE_CAMPO", label: "Supervisor de campo" },
  { value: "ADMIN", label: "Administrador" },
  { value: "ADMIN_RESTRITO", label: "Administrador restrito" },
  { value: "DP", label: "Departamento Pessoal" },
  { value: "FINANCEIRO", label: "Financeiro" },
  { value: "FINANCEIRO_LEITURA", label: "Financeiro (somente leitura)" },
  { value: "GESTOR_CAMPANHAS", label: "Gestor de campanhas" },
];

export const isRestrictedAdminToken = (token: string | null) =>
  getPrincipalRoleFromToken(token) === "ADMIN_RESTRITO";

/** O ator pode conceder este papel (como tipo ou acesso adicional)? */
export function canGrantRole(token: string | null, role: UserType): boolean {
  if (!isRestrictedAdminToken(token)) return true;
  if (isAdminType(role)) return false;
  const revoked = getRevokedModulesFromToken(token);
  return !(MODULES_OF_ROLE[role] ?? []).some((m) => revoked.includes(m));
}

/**
 * O ator pode editar, desativar ou reativar este usuário? O restrito não mexe em
 * administradores nem em quem alcança módulo que lhe foi retirado; a própria
 * conta ele edita como qualquer usuário.
 */
export function canManageUser(
  token: string | null,
  user: { id: string; userType: UserType; extraRoles?: UserType[] },
): boolean {
  if (!isRestrictedAdminToken(token)) return true;
  if (getAuthPayload(token)?.sub === user.id) return true;
  if (isAdminType(user.userType)) return false;
  return [user.userType, ...(user.extraRoles ?? [])].every((role) => canGrantRole(token, role));
}
